import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { trackClientEvent } from 'analytics/analytics'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { fetchOrders } from 'actions/OrderActions'
import { fetchUserReviewsByOrder } from 'actions/ReviewActions'
import { Experiments } from 'constants/experiments'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DAY_ONLY, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'
import { isSameMonth, isSameYear, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { checkOrderAcceptsReview, getReviewOrderDisplayDetails, getReviewOrderImage } from 'lib/order/reviewUtils'
import { last } from 'lib/array/arrayUtils'
import { getSortedOrdersByDeparture } from 'selectors/orderSelectors'
import { dismissPostTripReviewNudge, isPostTripReviewNudgeDismissed } from './postTripReviewNudgeStorage'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const RATING_SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const POST_TRIP_REVIEW_NUDGE_RECENT_DAYS = 30

function isRecentReviewableOrder(order: App.Order): boolean {
  const { endDate, startDate } = getReviewOrderDisplayDetails(order)
  const tripEndDate = endDate ?? startDate

  if (!tripEndDate) {
    return false
  }

  const daysSinceTripEnded = moment.utc().startOf('day').diff(moment.utc(tripEndDate).startOf('day'), 'days')

  return daysSinceTripEnded >= 0 && daysSinceTripEnded <= POST_TRIP_REVIEW_NUDGE_RECENT_DAYS
}

function checkOrderAcceptsPostTripReviewNudge(order: App.Order): boolean {
  return checkOrderAcceptsReview(order) && isRecentReviewableOrder(order)
}

function getStayedDateRangeLabel(startDateValue: string, endDateValue: string | undefined, dateFormatter: ReturnType<typeof useIntlDateFormatter>): string {
  if (!endDateValue || startDateValue === endDateValue) {
    return dateFormatter(startDateValue, INTL_DAY_MONTH_NAME_SHORT)
  }

  const startDate = new Date(startDateValue)
  const endDate = new Date(endDateValue)

  if (isSameMonth(startDate, endDate)) {
    return `${dateFormatter(startDateValue, INTL_DAY_ONLY)} - ${dateFormatter(endDateValue, INTL_DMY_CASUAL_SHORT_FORMAT)}`
  }

  if (isSameYear(startDate, endDate)) {
    return `${dateFormatter(startDateValue, INTL_DAY_MONTH_NAME_SHORT)} - ${dateFormatter(endDateValue, INTL_DMY_CASUAL_SHORT_FORMAT)}`
  }

  return `${dateFormatter(startDateValue, INTL_DMY_CASUAL_SHORT_FORMAT)} - ${dateFormatter(endDateValue, INTL_DMY_CASUAL_SHORT_FORMAT)}`
}

const SectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(32)};
  }
`

const Card = styled(Group)`
  position: relative;
  overflow: hidden;
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(136)};
    padding-inline-end: ${rem(72)};
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
  }
`

const ImageFrame = styled.div`
  overflow: hidden;
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.S};
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(144)};
    min-height: ${rem(104)};
  }
`

const RatingButton = styled(TextButton)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  width: ${rem(40)};
  height: ${rem(40)};


  span {
    text-decoration: none;
  }
`

const DismissButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  inset-inline-end: ${rem(8)};
`

function PostTripReviewNudge() {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const dispatch = useAppDispatch()
  const [isDismissed, setIsDismissed] = useState(isPostTripReviewNudgeDismissed)
  const [impressionRef, isBannerInView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const attemptedReviewOrderIdsRef = useRef<Set<string>>(new Set())

  const allOrdersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'all'))
  const orders = useAppSelector(state => getSortedOrdersByDeparture(state).data)
  const reviewsByOrder = useAppSelector(state => state.review.reviewsByOrder)
  const reviewsByOrderLoading = useAppSelector(state => state.review.reviewsByOrderLoading)

  const { isTurnedOn, isAnyVariant, triggerActivation } = useExperiment(Experiments.croPostTripReviewHomepage, { canActivate: false })
  const reviewableOrders = useMemo(() => orders.filter(checkOrderAcceptsPostTripReviewNudge), [orders])
  const reviewableOrderPendingReviews = useMemo(() => last(reviewableOrders.filter(order =>
    !reviewsByOrder[order.id] &&
    !reviewsByOrderLoading[order.id] &&
    !attemptedReviewOrderIdsRef.current.has(order.id),
  )), [reviewableOrders, reviewsByOrder, reviewsByOrderLoading])
  const nudgeOrder = useMemo(() => last(reviewableOrders.filter(order => reviewsByOrder[order.id]?.length === 0)), [reviewableOrders, reviewsByOrder])

  const tourItem = nudgeOrder?.tourItems[0]
  const experienceId = nudgeOrder?.experienceItems[0]?.experienceId
  const experience = useAppSelector(state => experienceId ? state.experience.experiences[experienceId] : undefined)
  const [tourSnapshot] = useTourV2Snapshot(tourItem?.reservationId, tourItem?.orderId)
  const reviewOrderImage = useMemo(() => nudgeOrder ? getReviewOrderImage(nudgeOrder, tourSnapshot?.imageId, experience) : undefined, [experience, nudgeOrder, tourSnapshot?.imageId])
  const reviewOrderDisplayDetails = useMemo(() => nudgeOrder ? getReviewOrderDisplayDetails(nudgeOrder, tourSnapshot, experience) : undefined, [experience, nudgeOrder, tourSnapshot])
  const hasReviewOrderImage = !!reviewOrderImage?.imageId || !!reviewOrderImage?.image
  const isNudgeEligible = !isDismissed && !!nudgeOrder && !!reviewOrderDisplayDetails?.title && hasReviewOrderImage

  const stayedDateLabel = useMemo(() => {
    if (!reviewOrderDisplayDetails?.startDate) {
      return undefined
    }

    return getStayedDateRangeLabel(
      reviewOrderDisplayDetails.startDate,
      reviewOrderDisplayDetails.endDate,
      dateFormatter,
    )
  }, [dateFormatter, reviewOrderDisplayDetails])

  const handleDismiss = useCallback(() => {
    if (!nudgeOrder) {
      return
    }

    dismissPostTripReviewNudge()
    setIsDismissed(true)
    trackClientEvent({
      subject: 'homepage_post_trip_review_nudge',
      action: 'dismiss',
      category: 'homepage',
      type: 'interaction',
    })
  }, [nudgeOrder])

  const handleLeaveReviewClick = useCallback(() => {
    trackClientEvent({
      subject: 'homepage_post_trip_review_nudge',
      action: 'leave_review_clicked',
      category: 'homepage',
      type: 'interaction',
    })
  }, [])

  const dismissLabel = intl.formatMessage({
    id: 'FRgiyfx6CHjKmit2b8qv',
    defaultMessage: 'Dismiss review prompt',
    description: 'Accessibility label for the button that hides the homepage post-trip review prompt',
  })
  const title = intl.formatMessage({
    id: 'RrPUhG0npoa007ywys8a',
    defaultMessage: 'How was {name}?',
    description: 'Heading text in the homepage prompt asking customers to rate a recently completed trip',
  }, { name: reviewOrderDisplayDetails?.title })
  const reviewDueLabel = intl.formatMessage({
    id: '4MYRP5jPWORFoclFdbdT',
    defaultMessage: 'Review due',
    description: 'Short label in the homepage review prompt indicating a review is due for a completed trip',
  })
  const scorePromptLabel = intl.formatMessage({
    id: 'JSacNm1_liuzdcV3LM1x',
    defaultMessage: 'Tap a score to begin',
    description: 'Instruction label above rating buttons in the homepage review prompt',
  })
  const ctaLabel = intl.formatMessage({
    id: '_egEE2ywR-NtNlRgpwJv',
    defaultMessage: 'Leave a review',
    description: 'Button label in the homepage post-trip prompt that opens the customer review form',
  })
  const terribleLabel = intl.formatMessage({
    id: 'B6xg4WIfCvsqfHzVOy_6',
    defaultMessage: 'Terrible',
    description: 'Default lowest rating label on number rating scale',
  })
  const outstandingLabel = intl.formatMessage({
    id: 'vGrrl3_B0A1-j8J23v20',
    defaultMessage: 'Outstanding',
    description: 'Default highest rating label on number rating scale',
  })

  useEffect(() => {
    if (!isDismissed && isTurnedOn && !allOrdersFetched) {
      dispatch(fetchOrders())
    }
  }, [allOrdersFetched, dispatch, isDismissed, isTurnedOn])

  useEffect(() => {
    if (isDismissed || !isTurnedOn || !allOrdersFetched || nudgeOrder) {
      return
    }

    if (reviewableOrderPendingReviews) {
      attemptedReviewOrderIdsRef.current.add(reviewableOrderPendingReviews.id)
      dispatch(fetchUserReviewsByOrder(reviewableOrderPendingReviews.id))
    }
  }, [allOrdersFetched, dispatch, isDismissed, isTurnedOn, nudgeOrder, reviewableOrderPendingReviews])

  useEffect(() => {
    if (!isDismissed && isTurnedOn && experienceId) {
      dispatch(fetchExperiencesById(experienceId))
    }
  }, [dispatch, experienceId, isDismissed, isTurnedOn])

  useEffect(() => {
    if (isTurnedOn && isNudgeEligible) {
      triggerActivation()
    }
  }, [isNudgeEligible, isTurnedOn, triggerActivation])

  useEffect(() => {
    if (isBannerInView) {
      trackClientEvent({
        subject: 'homepage_post_trip_review_nudge',
        action: 'impression',
        category: 'homepage',
        type: 'nonInteraction',
      })
    }
  }, [isBannerInView])

  if (!isAnyVariant || !isNudgeEligible) {
    return null
  }

  return (
    <SectionContainer ref={impressionRef}>
      <Card direction="vertical" tabletDirection="horizontal" gap={16} tabletGap={32} verticalAlign="center">
        <Group direction="horizontal" gap={16} tabletVerticalAlign="center" grow>
          <ImageFrame>
            {reviewOrderImage?.imageId && (
              <ResponsiveImage
                id={reviewOrderImage.imageId}
                mobileAspectRatio="1:1"
                desktopAspectRatio="16:9"
                alt=""
                fit="center"
              />
            )}
            {reviewOrderImage?.image && <Image image={reviewOrderImage.image} alt="" fit="center" width="100%" height="100%" />}
          </ImageFrame>
          <Group direction="vertical" gap={4} tabletGap={8} horizontalAlign="start">
            <PromotionLabel variant="default">{reviewDueLabel}</PromotionLabel>
            <Heading variant="heading5" as="h5">{title}</Heading>
            {stayedDateLabel && (
              <BodyTextBlock variant="medium" colour="light-foreground-alt" startIcon={<LineCalendarIcon />}>
                {stayedDateLabel}
              </BodyTextBlock>
            )}
          </Group>
        </Group>
        <CSSBreakpoint max="mobile">
          <TextButton kind="primary" fit="mobile-full-width" size="medium" to={`/review/${nudgeOrder.id}`} onClick={handleLeaveReviewClick}>
            {ctaLabel}
          </TextButton>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <Group direction="vertical" gap={4} horizontalAlign="stretch">
            <BodyText variant="medium" weight="bold">{scorePromptLabel}</BodyText>
            <Group direction="horizontal" gap={4} tabletGap={8} wrap="wrap">
              {RATING_SCORES.map(score => (
                <RatingButton
                  key={score}
                  kind="tertiary"
                  variant="default"
                  size="medium"
                  to={`/review/${nudgeOrder.id}?rating=${score}`}
                  onClick={handleLeaveReviewClick}
                >
                  {score}
                </RatingButton>
              ))}
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between">
              <Caption variant="medium" colour="light-foreground-alt">{terribleLabel}</Caption>
              <Caption variant="medium" colour="light-foreground-alt">{outstandingLabel}</Caption>
            </Group>
          </Group>
        </CSSBreakpoint>
        <DismissButton
          kind="tertiary"
          size="small"
          shape="circle"
          aria-label={dismissLabel}
          onClick={handleDismiss}
        >
          <LineTimesIcon />
        </DismissButton>
      </Card>
    </SectionContainer>
  )
}

export default PostTripReviewNudge
